import { useEffect, useState } from 'react'
import { Toast, type ToastProps } from './Toast'

interface ToastContainerProps {
    toasts: ToastProps[]
    onRemove: (id: string) => void
    /** When true, position at bottom-center (for map pages) */
    isMapPage?: boolean
}

export function ToastContainer({ toasts, onRemove, isMapPage = false }: ToastContainerProps) {
    const [exitingToasts, setExitingToasts] = useState<Set<string>>(new Set())

    useEffect(() => {
        // Auto-dismiss toasts after their duration
        const timers = toasts.map(toast => {
            if (!toast.duration || toast.duration <= 0) return null

            // Start exit animation
            const exitTimer = setTimeout(() => {
                setExitingToasts(prev => new Set(prev).add(toast.id))
            }, toast.duration - 300)

            // Remove toast
            const removeTimer = setTimeout(() => {
                onRemove(toast.id)
                setExitingToasts(prev => {
                    const next = new Set(prev)
                    next.delete(toast.id)
                    return next
                })
            }, toast.duration)

            return { exitTimer, removeTimer }
        })

        return () => {
            timers.forEach(timer => {
                if (timer) {
                    clearTimeout(timer.exitTimer)
                    clearTimeout(timer.removeTimer)
                }
            })
        }
    }, [toasts, onRemove])

    const handleClose = (id: string) => {
        setExitingToasts(prev => new Set(prev).add(id))
        setTimeout(() => {
            onRemove(id)
            setExitingToasts(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }, 300)
    }

    // Position classes based on page type
    const positionClasses = isMapPage
        ? 'fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-3 max-w-md'
        : 'fixed top-0 right-[5px] z-50 flex flex-col gap-3 max-w-md pt-2'

    return (
        <div className={positionClasses}>
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    {...toast}
                    isExiting={exitingToasts.has(toast.id)}
                    onClose={() => handleClose(toast.id)}
                />
            ))}
        </div>
    )
}
